import { clamp } from '../utils/math-helpers.js';

export function createFrameTaskScheduler({ defaultBudgetMs = 1.8 } = {}) {
  const tasks = new Map();
  let carryoverMs = 0;

  const snapshot = {
    budgetMs: 0,
    spentMs: 0,
    runs: 0,
    skips: 0,
    queued: 0
  };

  function registerTask(id, {
    run,
    minInterval = 0.05,
    maxInterval = 0.25,
    priority = 0
  } = {}) {
    if (!id || typeof run !== 'function') return;
    const now = performance.now() * 0.001;
    tasks.set(String(id), {
      id: String(id),
      run,
      minInterval: Math.max(0.001, Number(minInterval) || 0.05),
      maxInterval: Math.max(0.005, Number(maxInterval) || 0.25),
      priority: Number(priority) || 0,
      lastRunAt: now
    });
  }

  function unregisterTask(id) {
    tasks.delete(String(id));
  }

  function touchTask(id) {
    const task = tasks.get(String(id));
    if (!task) return false;
    task.lastRunAt = performance.now() * 0.001;
    return true;
  }

  function runFrame(frameDelta = 0.016, { budgetMs = defaultBudgetMs, force = false } = {}) {
    const now = performance.now() * 0.001;
    const baseBudget = Math.max(0, Number(budgetMs) || 0);
    let remainingMs = Math.max(0, baseBudget + carryoverMs);
    const due = [];
    snapshot.budgetMs = remainingMs;
    snapshot.spentMs = 0;
    snapshot.runs = 0;
    snapshot.skips = 0;

    for (const task of tasks.values()) {
      const elapsed = Math.max(0, now - task.lastRunAt);
      if (force || elapsed >= task.minInterval || elapsed >= task.maxInterval) {
        due.push({
          task,
          elapsed,
          urgent: elapsed >= task.maxInterval
        });
      }
    }

    due.sort((a, b) => {
      if (a.urgent !== b.urgent) return a.urgent ? -1 : 1;
      if (a.task.priority !== b.task.priority) return b.task.priority - a.task.priority;
      return b.elapsed - a.elapsed;
    });

    snapshot.queued = due.length;

    for (const entry of due) {
      const { task, elapsed, urgent } = entry;
      if (!urgent && remainingMs <= 0) {
        snapshot.skips += 1;
        continue;
      }

      const start = performance.now();
      try {
        task.run({
          frameDelta,
          elapsed
        });
      } catch (err) {
        console.error(`[frame-scheduler] task "${task.id}" failed`, err);
      }
      const spent = Math.max(0, performance.now() - start);
      snapshot.spentMs += spent;
      snapshot.runs += 1;
      task.lastRunAt = now;
      remainingMs -= spent;
    }

    carryoverMs = clamp(remainingMs, -baseBudget * 0.5, baseBudget * 0.5);
  }

  function getSnapshot() {
    return {
      budgetMs: snapshot.budgetMs,
      spentMs: snapshot.spentMs,
      runs: snapshot.runs,
      skips: snapshot.skips,
      queued: snapshot.queued,
      taskCount: tasks.size
    };
  }

  return {
    registerTask,
    unregisterTask,
    touchTask,
    runFrame,
    getSnapshot
  };
}
